import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PageHeader from "@/components/PageHeader";
import { supabase } from "@/integrations/supabase/client";
import DOMPurify from "dompurify";

interface Article {
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  image_url: string | null;
  image_alt: string | null;
  author_name: string;
  published_at: string;
  meta_description: string | null;
  json_ld: any;
}

const SITE = "https://aiconference.london";

const AutoNewsPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    supabase
      .from("news_articles")
      .select("*")
      .eq("slug", slug)
      .eq("status", "published")
      .maybeSingle()
      .then(async ({ data }) => {
        if (!data) {
          // Hidden repeat articles forward to their live version.
          const { data: redir } = await (supabase as any)
            .from("news_redirects")
            .select("to_slug")
            .eq("from_slug", slug)
            .maybeSingle();
          if (redir?.to_slug) {
            navigate(`/${redir.to_slug}`, { replace: true });
            return;
          }
        }
        setPost(data as any);
        setLoading(false);
      });
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A192F] via-[#172A46] to-[#1E3A5F]">
        <Header />
        <div className="container mx-auto px-4 py-32 text-center text-white">Loading…</div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A192F] via-[#172A46] to-[#1E3A5F]">
        <Helmet><title>Article not found - AI World Congress 2026 - November 25-26, London UK - AI Conference</title></Helmet>
        <Header />
        <div className="container mx-auto px-4 py-32 text-center">
          <h1 className="text-3xl font-bold text-white mb-4">Article not found</h1>
          <Link to="/news" className="text-orange-400 hover:underline">Back to AI News</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const url = `${SITE}/news/${post.slug}`;
  const ogImg = post.image_url || `${SITE}/lovable-uploads/og-banner-2026.jpg`;
  const desc = post.meta_description || post.excerpt;
  const dateStr = new Date(post.published_at).toLocaleDateString("en-GB", {
    year: "numeric", month: "long", day: "numeric",
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A192F] via-[#172A46] to-[#1E3A5F]">
      <Helmet>
        <title>{post.title} - AI World Congress 2026 - November 25-26, London UK - AI Conference</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={url} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={desc} />
        <meta property="og:image" content={ogImg} />
        <meta property="og:image:secure_url" content={ogImg} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={post.image_alt || post.title} />
        <meta property="og:site_name" content="AI Conference London 2026" />
        <meta property="article:published_time" content={post.published_at} />
        <meta property="article:author" content={post.author_name} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={desc} />
        <meta name="twitter:image" content={ogImg} />
        <meta name="twitter:image:alt" content={post.image_alt || post.title} />
        <script type="application/ld+json">{JSON.stringify(post.json_ld || {
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          "headline": post.title,
          "description": desc,
          "image": [ogImg],
          "datePublished": post.published_at,
          "dateModified": post.published_at,
          "author": { "@type": "Person", "name": post.author_name },
          "publisher": {
            "@type": "Organization",
            "name": "AI Conference London",
            "logo": { "@type": "ImageObject", "url": `${SITE}/lovable-uploads/logo-2026.svg` }
          },
          "mainEntityOfPage": { "@type": "WebPage", "@id": url },
          "articleSection": post.category,
          "url": url
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE },
            { "@type": "ListItem", "position": 2, "name": "News", "item": `${SITE}/news` },
            { "@type": "ListItem", "position": 3, "name": post.title, "item": url }
          ]
        })}</script>
      </Helmet>
      <Header />
      <PageHeader
        title=""
        subtitle=""
        backgroundImage="/lovable-uploads/75e6360c-a56e-48e7-9e3c-5903e26ee0a6.png"
      />
      <main className="container mx-auto px-4 py-16">
        <article className="max-w-4xl mx-auto">
          <div className="text-sm text-orange-400 mb-3">{post.category} • {dateStr} • By {post.author_name}</div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">{post.title}</h1>
          <p className="text-lg text-gray-300 mb-8 italic">{post.excerpt}</p>
          {post.image_url && (
            <img
              src={post.image_url}
              alt={post.image_alt || post.title}
              className="w-full h-auto rounded-lg mb-8 shadow-2xl"
              loading="eager"
            />
          )}
          <div
            className="prose prose-invert prose-lg max-w-none
              prose-headings:text-white prose-h2:text-3xl prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-xl prose-h3:mt-6 prose-h3:text-orange-300
              prose-p:text-gray-200 prose-p:leading-relaxed
              prose-a:text-orange-400 hover:prose-a:text-orange-300 prose-a:underline
              prose-li:text-gray-200 prose-strong:text-white prose-blockquote:text-gray-300"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content, { FORCE_BODY: true }) }}
          />
          <div className="mt-12 pt-8 border-t border-white/10 flex justify-between text-sm">
            <Link to="/news" className="text-orange-400 hover:underline">← All AI news</Link>
            <Link to="/register" className="text-orange-400 hover:underline">Register for AI World Congress 2026 →</Link>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default AutoNewsPost;
